/**
 * An RCS message's design, as the API keeps it on a template
 * (`app.modules.messaging.rcs.design`). The template's text — with its
 * {#var#} slots — is the message on a text layout, the card's description on a
 * card, and the first card's description on a carousel.
 *
 * The limits are Google's; the server checks them all again.
 */

export const LIMITS = {
  text: 3072,
  title: 200,
  description: 2000,
  suggestion: 25,
  messageSuggestions: 11,
  cardSuggestions: 4,
  carousel: [2, 10] as const,
  basic: 160,
} as const;

export type SuggestionType =
  | 'reply'
  | 'dial'
  | 'open_url'
  | 'view_location'
  | 'share_location'
  | 'calendar'
  | 'compose';

export const SUGGESTION_TYPES: { value: SuggestionType; label: string }[] = [
  { value: 'reply', label: 'Reply' },
  { value: 'dial', label: 'Call a number' },
  { value: 'open_url', label: 'Open a link' },
  { value: 'view_location', label: 'Show a place' },
  { value: 'share_location', label: 'Ask for their location' },
  { value: 'calendar', label: 'Add a calendar event' },
  { value: 'compose', label: 'Start a message' },
];

export interface Suggestion {
  type: SuggestionType;
  text: string;
  postback?: string | null;
  phone?: string | null;
  url?: string | null;
  open_in?: 'browser' | 'webview_full' | 'webview_half' | 'webview_tall';
  latitude?: number | null;
  longitude?: number | null;
  query?: string | null;
  label?: string | null;
  start?: string | null;
  end?: string | null;
  title?: string | null;
  description?: string | null;
  message?: string | null;
  fallback_url?: string | null;
}

export interface Media {
  url: string;
  thumbnail_url?: string | null;
  height: 'SHORT' | 'MEDIUM' | 'TALL';
}

export interface Card {
  title?: string | null;
  description?: string | null;
  media?: Media | null;
  suggestions: Suggestion[];
}

export interface RcsDesign {
  layout: 'text' | 'card' | 'carousel';
  card?: Card | null;
  orientation: 'VERTICAL' | 'HORIZONTAL';
  image_alignment: 'LEFT' | 'RIGHT';
  cards: Card[];
  card_width: 'SMALL' | 'MEDIUM';
  suggestions: Suggestion[];
  ttl_hours?: number | null;
  /** An SMS template of the same client, with as many slots, sent instead to a
   * phone that cannot receive RCS — the same values, in the same order. */
  sms_fallback?: string | null;
}

export function emptyDesign(): RcsDesign {
  return {
    layout: 'text',
    card: null,
    orientation: 'VERTICAL',
    image_alignment: 'LEFT',
    cards: [],
    card_width: 'MEDIUM',
    suggestions: [],
    ttl_hours: null,
    sms_fallback: null,
  };
}

export function emptyCard(): Card {
  return { title: '', description: '', media: null, suggestions: [] };
}

/** A design as the API returned it, with every field present. */
export function designFrom(raw: Record<string, unknown> | null | undefined): RcsDesign {
  const base = emptyDesign();
  if (!raw) return base;
  const design = { ...base, ...(raw as Partial<RcsDesign>) };
  return {
    ...design,
    cards: design.cards ?? [],
    suggestions: design.suggestions ?? [],
    card: design.card ? { ...emptyCard(), ...design.card } : null,
  };
}

/** How much text the layout holds where it puts the template's text. */
export function textLimit(design: RcsDesign): number {
  return design.layout === 'text' ? LIMITS.text : LIMITS.description;
}

const HTTPS = /^https:\/\/\S+$/;
const PHONE = /^\+?[0-9]{6,15}$/;

function suggestionProblem(item: Suggestion, where: string): string | null {
  if (!item.text.trim()) return `${where}: give it a label.`;
  if (item.text.length > LIMITS.suggestion) return `${where}: the label is longer than ${LIMITS.suggestion} characters.`;
  switch (item.type) {
    case 'dial':
    case 'compose':
      return PHONE.test(item.phone ?? '') ? null : `${where}: a phone number, like +919876543210.`;
    case 'open_url':
      return HTTPS.test(item.url ?? '') ? null : `${where}: an https:// address.`;
    case 'view_location': {
      const point = item.latitude != null && item.longitude != null;
      return point || item.query?.trim() ? null : `${where}: a latitude and longitude, or a search.`;
    }
    case 'calendar':
      if (!item.start || !item.end || !item.title?.trim()) return `${where}: a start, an end and a title.`;
      return new Date(item.end) > new Date(item.start) ? null : `${where}: the event must end after it starts.`;
    default:
      return null;
  }
}

function cardProblems(card: Card, where: string, needsContent: boolean): string[] {
  const out: string[] = [];
  if ((card.title ?? '').length > LIMITS.title) out.push(`${where}: the title is longer than ${LIMITS.title} characters.`);
  if (card.media && !HTTPS.test(card.media.url)) out.push(`${where}: the picture or video needs an https:// address.`);
  if (card.media?.thumbnail_url && !HTTPS.test(card.media.thumbnail_url))
    out.push(`${where}: the thumbnail needs an https:// address.`);
  if (card.suggestions.length > LIMITS.cardSuggestions) out.push(`${where}: at most ${LIMITS.cardSuggestions} suggestions.`);
  card.suggestions.forEach((s, i) => {
    const problem = suggestionProblem(s, `${where}, suggestion ${i + 1}`);
    if (problem) out.push(problem);
  });
  if (needsContent && !(card.title?.trim() || card.description?.trim() || card.media))
    out.push(`${where} is empty.`);
  return out;
}

/** What stops the design being saved, in words. */
export function designProblems(design: RcsDesign, text: string): string[] {
  const out: string[] = [];
  const limit = textLimit(design);
  if (text.length > limit) out.push(`The text is ${text.length} characters; a ${design.layout} holds ${limit}.`);
  if (design.layout === 'card') {
    if (!design.card) out.push('Set up the card.');
    else out.push(...cardProblems(design.card, 'The card', false));
  }
  if (design.layout === 'carousel') {
    const [low, high] = LIMITS.carousel;
    if (design.cards.length < low || design.cards.length > high) out.push(`A carousel has ${low} to ${high} cards.`);
    design.cards.forEach((card, i) => out.push(...cardProblems(card, `Card ${i + 1}`, i > 0)));
  }
  if (design.suggestions.length > LIMITS.messageSuggestions)
    out.push(`At most ${LIMITS.messageSuggestions} suggestions under the message.`);
  design.suggestions.forEach((s, i) => {
    const problem = suggestionProblem(s, `Suggestion ${i + 1}`);
    if (problem) out.push(problem);
  });
  return out;
}

/** What Google bills it as: plain text up to 160 characters is a basic message. */
export function billingClass(design: RcsDesign, text: string): 'basic' | 'single' {
  return design.layout === 'text' && design.suggestions.length === 0 && text.length <= LIMITS.basic ? 'basic' : 'single';
}

/** The design as the API takes it: empty optional fields left out. */
export function designPayload(design: RcsDesign): Record<string, unknown> {
  const clean = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(clean);
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([, v]) => v !== '' && v !== null && v !== undefined)
          .map(([k, v]) => [k, clean(v)]),
      );
    }
    return value;
  };
  const base: Partial<RcsDesign> = { ...design };
  if (design.layout !== 'card') base.card = null;
  if (design.layout !== 'carousel') base.cards = [];
  return clean(base) as Record<string, unknown>;
}
